import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PencilSimple, PushPin } from '@phosphor-icons/react';
import { Button, Page, notify } from 'design';
import { getStoredUser } from '../utils/auth';
import { ADMIN_ITEM, canSee, visibleGroups } from '../shell/navConfig';
import PickQueueBadge from './inventory/PickQueueBadge';
import {
  MAX_HOME_PINS,
  initialPins,
  pinsToAdopt,
  rememberAccountPins,
  savePinsToAccount,
  togglePin,
  visiblePins,
  writeLocalPins,
} from './homePins.mjs';
import './Dashboard.css';

function greetingFor(date) {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

// Pins show at once and save in the background: to the account when it can store them, otherwise
// on this computer. Only the latest choice is sent; if saving fails the last saved pins come back.
function useHomePins(user, tools) {
  const email = user?.email;
  const accessToken = user?.accessToken;
  const [stored, setStored] = useState(() => initialPins(user));
  const pins = useMemo(() => visiblePins(stored, tools), [stored, tools]);
  const latest = useRef(pins);
  latest.current = pins;
  const confirmed = useRef(pins);
  const wanted = useRef(null);
  const saving = useRef(false);
  const adopted = useRef(false);

  const flush = useCallback(async () => {
    if (saving.current) return;
    saving.current = true;
    try {
      while (wanted.current) {
        const target = wanted.current;
        wanted.current = null;
        try {
          const result = await savePinsToAccount(target, { accessToken });
          confirmed.current = result.where === 'account' ? result.pins : target;
          if (result.where === 'account') rememberAccountPins(result.pins);
        } catch {
          wanted.current = null;
          setStored(confirmed.current);
          writeLocalPins(email, confirmed.current);
          notify("Couldn't save your pins. Try again.", { tone: 'error' });
        }
      }
    } finally {
      saving.current = false;
    }
  }, [accessToken, email]);

  // The account's pins are kept on this computer too, for when the server cannot be reached.
  // Pins made here before the account could store them move to the account once.
  useEffect(() => {
    if (adopted.current) return;
    adopted.current = true;
    if (Array.isArray(user?.homePins)) {
      writeLocalPins(email, user.homePins);
      return;
    }
    const local = pinsToAdopt(user);
    if (!local || !accessToken) return;
    savePinsToAccount(visiblePins(local, tools), { accessToken })
      .then((result) => {
        if (result.where !== 'account') return;
        confirmed.current = result.pins;
        rememberAccountPins(result.pins);
      })
      .catch(() => {});
  }, [accessToken, email, tools, user]);

  const toggle = useCallback((path) => {
    const { pins: next, refused } = togglePin(latest.current, path);
    if (refused) return false;
    latest.current = next;
    setStored(next);
    writeLocalPins(email, next);
    wanted.current = next;
    flush();
    return true;
  }, [email, flush]);

  return { pins, toggle };
}

function ToolTile({ item, user }) {
  const Icon = item.icon;
  return (
    <Link to={item.path} className="rc-tile dash-tool">
      <span className="rc-tile__icon">{Icon ? <Icon size={20} aria-hidden="true" /> : null}</span>
      <span className="dash-tool__text">
        <span className="rc-tile__title">
          <span>{item.label}</span>
          {item.badge === 'picks' ? <PickQueueBadge user={user} /> : null}
        </span>
        <span className="rc-tile__text">{item.description}</span>
      </span>
      <span className="dash-tool__foot" aria-hidden="true">
        <span className="dash-tool__path">{item.path}</span>
        <span className="rc-cta">Open<ArrowRight size={14} /></span>
      </span>
    </Link>
  );
}

// While choosing pins every tool is a toggle instead of a link.
function PinTile({ item, pinned, disabled, onToggle }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      className="rc-tile dash-tool dash-tool--pin"
      aria-pressed={pinned}
      aria-disabled={disabled || undefined}
      aria-label={`Pin ${item.label}`}
      onClick={() => { if (!disabled) onToggle(item.path); }}
    >
      <span className="rc-tile__icon">{Icon ? <Icon size={20} aria-hidden="true" /> : null}</span>
      <span className="dash-tool__text">
        <span className="rc-tile__title"><span>{item.label}</span></span>
        <span className="rc-tile__text">{item.description}</span>
      </span>
      <span className="dash-tool__foot" aria-hidden="true">
        <span className="dash-tool__path">{item.path}</span>
        <span className="dash-pin-state">{pinned ? 'Pinned' : 'Pin'}</span>
      </span>
      <span className="dash-pin-badge" aria-hidden="true"><PushPin size={16} /></span>
    </button>
  );
}

function Tile({ item, user, choosing }) {
  if (!choosing) return <ToolTile item={item} user={user} />;
  const pinned = choosing.pins.includes(item.path);
  return (
    <PinTile
      item={item}
      pinned={pinned}
      disabled={!pinned && choosing.pins.length >= MAX_HOME_PINS}
      onToggle={choosing.toggle}
    />
  );
}

function ToolGroup({ id, label, items, user, choosing }) {
  return (
    <section className="dash-group" aria-labelledby={`dash-${id}`}>
      <div className="dash-group__head">
        <h2 id={`dash-${id}`} className="rc-eyebrow dash-group__title">{label}</h2>
        <span className="dash-group__rule" aria-hidden="true" />
        <span className="dash-group__count">{items.length === 1 ? '1 tool' : `${items.length} tools`}</span>
      </div>
      <div className="dash-grid">
        {items.map((item) => <Tile key={item.path} item={item} user={user} choosing={choosing} />)}
      </div>
    </section>
  );
}

function PinnedSection({ items, user, pins, choosing, onEdit, onDone, editRef, hintRef }) {
  const head = (extra) => (
    <div className="dash-group__head">
      <h2 id="dash-pinned" className="rc-eyebrow dash-group__title">Pinned</h2>
      <span className="dash-group__rule" aria-hidden="true" />
      {extra}
    </div>
  );

  if (!choosing && !items.length) {
    return (
      <section className="dash-group dash-pins" aria-labelledby="dash-pinned">
        {head(null)}
        <button type="button" ref={editRef} className="dash-pins__empty" onClick={onEdit}>
          <PushPin size={20} aria-hidden="true" />
          <span>Pin up to four tools you use most to keep them here</span>
        </button>
      </section>
    );
  }

  const full = pins.length >= MAX_HOME_PINS;
  return (
    <section className="dash-group dash-pins" aria-labelledby="dash-pinned">
      {head(
        <>
          <span className="dash-group__count">{`${pins.length} of ${MAX_HOME_PINS}`}</span>
          {choosing ? (
            <Button size="sm" variant="primary" className="dash-pins__done" onClick={onDone}>Done</Button>
          ) : (
            <button type="button" ref={editRef} className="rc-btn rc-btn--ghost rc-btn--sm" onClick={onEdit}>
              <PencilSimple size={16} aria-hidden="true" />
              Edit pins
            </button>
          )}
        </>
      )}
      {choosing ? (
        <p ref={hintRef} tabIndex={-1} className="dash-pins__hint" aria-live="polite">
          {full ? 'All four spots are used. Unpin one to add another.' : 'Tap a tool to pin or unpin it. Up to four.'}
        </p>
      ) : null}
      <div className="dash-grid dash-pins__grid">
        {items.map((item) => <Tile key={item.path} item={item} user={user} choosing={choosing} />)}
        {choosing
          ? Array.from({ length: MAX_HOME_PINS - items.length }, (_, index) => (
            <div key={`spot-${index}`} className="dash-pins__spot" aria-hidden="true">Empty spot</div>
          ))
          : null}
      </div>
    </section>
  );
}

export default function Dashboard() {
  const user = useMemo(() => getStoredUser(), []);
  const firstName = String(user?.name || user?.email || 'Staff').trim().split(/\s+/)[0];
  const groups = useMemo(() => visibleGroups(user), [user]);
  const admin = canSee(user, ADMIN_ITEM) ? ADMIN_ITEM : null;
  const tools = useMemo(() => [...groups.flatMap((group) => group.items), ...(admin ? [admin] : [])], [groups, admin]);
  const { pins, toggle } = useHomePins(user, tools);
  const pinnedItems = useMemo(() => pins.map((path) => tools.find((tool) => tool.path === path)).filter(Boolean), [pins, tools]);
  const [editing, setEditing] = useState(false);
  const choosing = editing ? { pins, toggle } : null;
  const editRef = useRef(null);
  const hintRef = useRef(null);
  const firstRender = useRef(true);
  const now = new Date();
  const today = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }).format(now);

  // Escape finishes choosing pins.
  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setEditing(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing]);

  // Focus follows the mode: the instructions when choosing starts, the edit control when it ends.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    (editing ? hintRef.current : editRef.current)?.focus();
  }, [editing]);

  const startEditing = () => setEditing(true);
  const stopEditing = () => setEditing(false);

  return (
    <Page className="dash">
      <header className="dash-hero">
        <p className="rc-page-eyebrow dash-hero__eyebrow">
          <span>RandCards staff</span>
          <span className="dash-hero__date">{today}</span>
        </p>
        <h1 className="rc-page-title dash-hero__title">{`${greetingFor(now)}, ${firstName}`}</h1>
        <p className="rc-page-description">Every tool you can open, grouped like the menu. Press Cmd or Ctrl and K to jump straight to one.</p>
      </header>
      <PinnedSection
        items={pinnedItems}
        user={user}
        pins={pins}
        choosing={choosing}
        onEdit={startEditing}
        onDone={stopEditing}
        editRef={editRef}
        hintRef={hintRef}
      />
      {groups.map((group) => <ToolGroup key={group.id} id={group.id} label={group.label} items={group.items} user={user} choosing={choosing} />)}
      {admin ? <ToolGroup id="admin" label="Admin" items={[admin]} user={user} choosing={choosing} /> : null}
      {editing ? (
        <div className="rc-actionbar dash-pins__bar">
          <Button variant="primary" onClick={stopEditing}>Done</Button>
        </div>
      ) : null}
    </Page>
  );
}
